import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { Lead } from '@/shared/types'
import { apiFetch } from '@/shared/api/core'
import { spliceLeadIntoCache } from '@/shared/api/leadLists'

// ── contacts (BDR overrides on top of the canonical contacts table) ───────

export interface ContactPatch {
  name?: string
  role?: string
  email?: string
  phone?: string
  linkedin?: string
}

interface ContactPatchVars {
  leadId: string
  contactKey: string
  edits: ContactPatch
}

interface ContactCreateVars {
  leadId: string
  fields: ContactPatch & { name: string }
}

interface ContactDeleteVars {
  leadId: string
  contactKey: string
}

// PATCH /api/leads/{id}/contacts/{key} — overlay per-field edits on top
// of the canonical contact (or a previously-added local contact). Server
// returns the refreshed Lead; splice it directly into the cache.
export function usePatchContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ leadId, contactKey, edits }: ContactPatchVars) =>
      apiFetch<Lead>(`/api/leads/${leadId}/contacts/${encodeURIComponent(contactKey)}`, {
        method: 'PATCH',
        body: edits,
      }),
    onSuccess: (lead) => {
      spliceLeadIntoCache(qc, lead)
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// POST /api/leads/{id}/contacts — append a CRM-local contact. Server
// returns the refreshed lead with the new contact appearing at the end of
// decisionMakers (id `local:<uuid>`).
export function useAddContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ leadId, fields }: ContactCreateVars) =>
      apiFetch<Lead>(`/api/leads/${leadId}/contacts`, { method: 'POST', body: fields }),
    onSuccess: (lead) => {
      spliceLeadIntoCache(qc, lead)
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// DELETE /api/leads/{id}/contacts/{key} — tombstone (DB contact) or
// remove outright (local contact).
export function useDeleteContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ leadId, contactKey }: ContactDeleteVars) =>
      apiFetch<Lead>(`/api/leads/${leadId}/contacts/${encodeURIComponent(contactKey)}`, {
        method: 'DELETE',
      }),
    onSuccess: (lead) => {
      spliceLeadIntoCache(qc, lead)
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}
